/**
 * Canonical role → permission matrix (03-07 §2).
 *
 * Source of truth: `AdminAPI/Data/DatabaseSeeder.cs` — `SeedRolesAsync` and
 * `SeedPermissionsAsync` — on the backend's **`development`** branch. Resynced 2026-08-30
 * (HS-1384) and re-verified 2026-08-31 directly against a checkout of `sash-admin-api`
 * `development` @ `b5d9a84` (HS-1382 rebase): `SeedRolesAsync` seeds exactly these 12
 * human-holdable roles (+3 non-human technical identities), `SeedPermissionsAsync` seeds 95
 * permission keys, and every `AddMandatory`/`AddDefaultEnabled`/`AddOptional` call in
 * `SeedPermissionsAsync` was diffed programmatically against every role's `permissions` array
 * below — the two sets are identical, role for role, key for key.
 *
 * A prior header on this file cited a re-verification against `development` @ `6738e93`
 * (HS-1387, dated 2026-08-31) and shipped a narrower 6-role/64-key catalogue from that check.
 * `6738e93` is dated **2026-08-18** — 13 days and 57 commits behind the tip that check claimed
 * to be current against, predating the `user_roles_` merge (PR #77) and several seeder fixes.
 * That narrower catalogue was reverted here in favour of this resync, which matches the actual
 * current backend.
 *
 * `Institution.Delete` is the one permission key the seeder defines that this catalogue omits:
 * confirmed no role's `AddMandatory`/`AddDefaultEnabled`/`AddOptional` call grants it (not even
 * `SmartSashAdmin`, which gets there only through the `FullAccess` wildcard), so there is nothing
 * for the FE to show a switch for.
 *
 * One deliberate deviation from the seeder: `DeviceUser` carries no `Report.View` (HS-961). The
 * roles matrix (Confluence 266731521) gives reports to Institution Admin, Group Manager and Lab
 * Manager (view + export), Lab Assistant (view only) and nobody else among them; the developer
 * ruled that matrix wins where the seeder's `AddDefaultEnabled` disagrees.
 *
 * Closes the gap HS-863 left open ("Matrix frozen post-Q-01 confirmation" unticked): the
 * catalogue now carries all **12 human-holdable roles** (11 hierarchy/functional roles + Fume
 * Hood Certifier), matching HS-863's stated canonical target. Three backend "roles" are
 * deliberately absent — `SashDevice`, `Authenticated`, `FunctionAuthorization` — none of them
 * hold a permission and none is ever assigned to a person; `isRoleName()` already rejects them.
 *
 * Not resolved by this resync, flagged as a separate, later call:
 * - `RoleCode`/`ROLE_CODES`/`parseRoleCodes` (the single-letter `H|T|L`-style scheme) has zero
 *   consumers in `apps/admin`/`apps/portal` today — both apps read role/permission **names**
 *   (`isRoleName`, `toPermissionKeys`), not codes, and HS-1382 confirmed the live DTOs
 *   (`UserRoleViewDTO.roleName`, `UserDTO.roles: string[]`) carry names. Left in place, renamed
 *   only where an existing code's role was renamed; the 5 newly-added roles get no code.
 * - `ROLE_HIERARCHY`/`roleRank` once mirrored `UserController.RolePriority`, which computed the
 *   now-retired single `UserDTO.role`; HS-1565 deleted `RolePriority`, and the rank that governs
 *   role assignment lives only in the backend now (see `ROLE_HIERARCHY`). Renamed in place (same
 *   6 roles, same relative order) and not extended to the 5 new roles.
 *
 * Normative: these checks are UI show/hide ONLY — the server is authoritative and
 * every guard mirrors a backend check (03-07 §1).
 */

export type PermissionKey =
  | 'AccessControl.Check'
  | 'Auth.Login.Admin'
  | 'Auth.Login.Device'
  | 'Auth.Login.Portal'
  | 'Auth.Refresh'
  | 'Auth.TwoFactor'
  | 'Billing.Export'
  | 'Billing.View'
  | 'Calibration.View'
  | 'Calibration.Write'
  | 'Camera.GrantTechAccess'
  | 'Camera.ViewOther'
  | 'Camera.ViewOwn'
  | 'Certification.Create'
  | 'Certification.Delete'
  | 'Certification.Update'
  | 'Certification.ViewAll'
  | 'Certification.ViewOwn'
  | 'CertificationProgram.Manage'
  | 'CertificationProgram.ViewAll'
  | 'CertificationRecord.Finalize'
  | 'CertifierVisit.Create'
  | 'CertifierVisit.Manage'
  | 'Device.Create'
  | 'Device.Delete'
  | 'Device.Register'
  | 'Device.Unlock'
  | 'Device.Update'
  | 'Device.View'
  | 'Device.ViewAll'
  | 'Diagnostics.View'
  | 'File.Delete'
  | 'File.Upload'
  | 'FullAccess'
  | 'Group.Manage'
  | 'Group.View'
  | 'Installation.AcceptanceTest'
  | 'Installation.Configure'
  | 'Installation.View'
  | 'Institution.AdministerHierarchy'
  | 'Institution.Create'
  | 'Institution.Manage'
  | 'Institution.ManageStoragePool'
  | 'Institution.Update'
  | 'Institution.View'
  | 'Institution.ViewAggregates'
  | 'Institution.ViewAll'
  | 'Lab.Create'
  | 'Lab.Delete'
  | 'Lab.Update'
  | 'Lab.View'
  | 'Lab.ViewAll'
  | 'LanVav.Configure'
  | 'LanVav.View'
  | 'Maintenance.Manage'
  | 'Maintenance.View'
  | 'Permission.Assign'
  | 'Permission.ViewAll'
  | 'Permission.ViewOwn'
  | 'Recording.ManageOther'
  | 'Recording.ViewOwn'
  | 'RemoteAccess.View'
  | 'Report.Export'
  | 'Report.View'
  | 'Request.Manage'
  | 'Request.View'
  | 'Rfid.AssignCard'
  | 'Rfid.CheckStatus'
  | 'Rfid.CreateCard'
  | 'Rfid.RemoveCard'
  | 'Rfid.ViewAll'
  | 'Rfid.ViewOwn'
  | 'RfidOverride.Execute'
  | 'SafetyDocument.Edit'
  | 'SafetyLog.Export'
  | 'SafetyLog.View'
  | 'SafetyNotification.Publish'
  | 'SashOverride.Execute'
  | 'Scope.Assign'
  | 'Scope.ViewAll'
  | 'Scope.ViewOwn'
  | 'Session.Close'
  | 'Session.CloseAny'
  | 'Session.Extend'
  | 'Session.Start'
  | 'Session.View'
  | 'Session.ViewAll'
  | 'System.Seed'
  | 'System.ViewLogs'
  | 'TeachingStream.Control'
  | 'Training.Manage'
  | 'Training.View'
  | 'User.Create'
  | 'User.Delete'
  | 'User.ManageRoles'
  | 'User.Update'
  | 'User.UpdateOwn'
  | 'User.ViewAll'
  | 'User.ViewOwn'
  | 'Video.Observer'
  | 'Video.Presenter'
  | 'Video.PrivateStreamer';

export interface PermissionDef {
  key: PermissionKey;
  /** Human-readable name from the backend permission catalog. */
  label: string;
}

/** The permission catalog (key → label), mirroring `SeedPermissionsAsync`. */
export const PERMISSIONS: Record<PermissionKey, PermissionDef> = {
  'AccessControl.Check': { key: 'AccessControl.Check', label: 'Check Access' },
  'Auth.Login.Admin': { key: 'Auth.Login.Admin', label: 'Admin Login' },
  'Auth.Login.Device': { key: 'Auth.Login.Device', label: 'Device Login' },
  'Auth.Login.Portal': { key: 'Auth.Login.Portal', label: 'Portal Login' },
  'Auth.Refresh': { key: 'Auth.Refresh', label: 'Refresh Token' },
  'Auth.TwoFactor': { key: 'Auth.TwoFactor', label: 'Two-Factor Auth' },
  'Billing.Export': { key: 'Billing.Export', label: 'Export Billing' },
  'Billing.View': { key: 'Billing.View', label: 'View Billing' },
  'Calibration.View': { key: 'Calibration.View', label: 'View Calibration' },
  'Calibration.Write': { key: 'Calibration.Write', label: 'Write Calibration' },
  'Camera.GrantTechAccess': {
    key: 'Camera.GrantTechAccess',
    label: 'Grant Tech Support Camera Access',
  },
  'Camera.ViewOther': { key: 'Camera.ViewOther', label: 'View Other User Camera' },
  'Camera.ViewOwn': { key: 'Camera.ViewOwn', label: 'View Own Camera' },
  'Certification.Create': { key: 'Certification.Create', label: 'Issue Certification' },
  'Certification.Delete': { key: 'Certification.Delete', label: 'Revoke Certification' },
  'Certification.Update': { key: 'Certification.Update', label: 'Update Certification' },
  'Certification.ViewAll': { key: 'Certification.ViewAll', label: 'View All Certifications' },
  'Certification.ViewOwn': { key: 'Certification.ViewOwn', label: 'View Own Certifications' },
  'CertificationProgram.Manage': {
    key: 'CertificationProgram.Manage',
    label: 'Manage Certification Programs',
  },
  'CertificationProgram.ViewAll': {
    key: 'CertificationProgram.ViewAll',
    label: 'View Certification Programs',
  },
  'CertificationRecord.Finalize': {
    key: 'CertificationRecord.Finalize',
    label: 'Finalize Certification Record',
  },
  'CertifierVisit.Create': { key: 'CertifierVisit.Create', label: 'Create Certifier Visit' },
  'CertifierVisit.Manage': { key: 'CertifierVisit.Manage', label: 'Manage Certifier Visit' },
  'Device.Create': { key: 'Device.Create', label: 'Create Device' },
  'Device.Delete': { key: 'Device.Delete', label: 'Delete Device' },
  'Device.Register': { key: 'Device.Register', label: 'Register Device' },
  'Device.Unlock': { key: 'Device.Unlock', label: 'Unlock Device' },
  'Device.Update': { key: 'Device.Update', label: 'Update Device' },
  'Device.View': { key: 'Device.View', label: 'View Device' },
  'Device.ViewAll': { key: 'Device.ViewAll', label: 'View All Devices' },
  'Diagnostics.View': { key: 'Diagnostics.View', label: 'View Diagnostics' },
  // HS-1484: My Files. Both are granted to every human role and neither is mandatory, so an
  // institution can revoke either one — which is what makes the portal's 403 branch reachable.
  'File.Delete': { key: 'File.Delete', label: 'Delete Files' },
  'File.Upload': { key: 'File.Upload', label: 'Upload Files' },
  FullAccess: { key: 'FullAccess', label: 'Full access to the system' },
  'Group.Manage': { key: 'Group.Manage', label: 'Manage Groups' },
  'Group.View': { key: 'Group.View', label: 'View Group' },
  'Installation.AcceptanceTest': {
    key: 'Installation.AcceptanceTest',
    label: 'Run Acceptance Test',
  },
  'Installation.Configure': { key: 'Installation.Configure', label: 'Configure Installation' },
  // HS-1543: gates `GET /api/Installation`; HS-980 reads the admin Installations section with it.
  'Installation.View': { key: 'Installation.View', label: 'View Installations' },
  'Institution.AdministerHierarchy': {
    key: 'Institution.AdministerHierarchy',
    label: 'Administer Institution Hierarchy',
  },
  'Institution.Create': { key: 'Institution.Create', label: 'Create Institution' },
  'Institution.Manage': { key: 'Institution.Manage', label: 'Manage Institutions' },
  // HS-1507/HS-1508: granted to no role — only SmartSashAdmin holds it, through FullAccess. It is in
  // the catalogue (unlike `Institution.Delete`) because the storage pool field gates on it.
  'Institution.ManageStoragePool': {
    key: 'Institution.ManageStoragePool',
    label: 'Manage Institution Storage Pool',
  },
  'Institution.Update': { key: 'Institution.Update', label: 'Update Institution' },
  'Institution.View': { key: 'Institution.View', label: 'View Institution' },
  'Institution.ViewAggregates': {
    key: 'Institution.ViewAggregates',
    label: 'View Institution Aggregates',
  },
  'Institution.ViewAll': { key: 'Institution.ViewAll', label: 'View All Institutions' },
  'Lab.Create': { key: 'Lab.Create', label: 'Create Lab' },
  'Lab.Delete': { key: 'Lab.Delete', label: 'Delete Lab' },
  'Lab.Update': { key: 'Lab.Update', label: 'Update Lab' },
  'Lab.View': { key: 'Lab.View', label: 'View Lab' },
  'Lab.ViewAll': { key: 'Lab.ViewAll', label: 'View All Labs' },
  'LanVav.Configure': { key: 'LanVav.Configure', label: 'Configure LAN/VAV' },
  'LanVav.View': { key: 'LanVav.View', label: 'View LAN/VAV' },
  'Maintenance.Manage': { key: 'Maintenance.Manage', label: 'Manage Maintenance' },
  'Maintenance.View': { key: 'Maintenance.View', label: 'View Maintenance' },
  'Permission.Assign': { key: 'Permission.Assign', label: 'Assign Permissions' },
  'Permission.ViewAll': { key: 'Permission.ViewAll', label: 'View All Permissions' },
  'Permission.ViewOwn': { key: 'Permission.ViewOwn', label: 'View Own Permissions' },
  'Recording.ManageOther': { key: 'Recording.ManageOther', label: 'Manage Other Recordings' },
  'Recording.ViewOwn': { key: 'Recording.ViewOwn', label: 'View Own Recordings' },
  'RemoteAccess.View': { key: 'RemoteAccess.View', label: 'Remote Access' },
  'Report.Export': { key: 'Report.Export', label: 'Export Reports' },
  'Report.View': { key: 'Report.View', label: 'View Reports' },
  // HS-1548: service requests. Mandatory for InstitutionAdmin (both) and LabManager (View).
  'Request.Manage': { key: 'Request.Manage', label: 'Manage Service Requests' },
  'Request.View': { key: 'Request.View', label: 'View Service Requests' },
  'Rfid.AssignCard': { key: 'Rfid.AssignCard', label: 'Assign RFID Card' },
  'Rfid.CheckStatus': { key: 'Rfid.CheckStatus', label: 'Check RFID Status' },
  'Rfid.CreateCard': { key: 'Rfid.CreateCard', label: 'Create RFID Card' },
  'Rfid.RemoveCard': { key: 'Rfid.RemoveCard', label: 'Remove RFID Card' },
  'Rfid.ViewAll': { key: 'Rfid.ViewAll', label: 'View All RFID Cards' },
  'Rfid.ViewOwn': { key: 'Rfid.ViewOwn', label: 'View Own RFID Cards' },
  'RfidOverride.Execute': { key: 'RfidOverride.Execute', label: 'Execute RFID Override' },
  'SafetyDocument.Edit': { key: 'SafetyDocument.Edit', label: 'Edit Safety Documents' },
  'SafetyLog.Export': { key: 'SafetyLog.Export', label: 'Export Safety Logs' },
  'SafetyLog.View': { key: 'SafetyLog.View', label: 'View Safety Logs' },
  'SafetyNotification.Publish': {
    key: 'SafetyNotification.Publish',
    label: 'Publish Safety Notification',
  },
  'SashOverride.Execute': { key: 'SashOverride.Execute', label: 'Execute Sash Override' },
  'Scope.Assign': { key: 'Scope.Assign', label: 'Assign Scopes' },
  'Scope.ViewAll': { key: 'Scope.ViewAll', label: 'View All Scopes' },
  'Scope.ViewOwn': { key: 'Scope.ViewOwn', label: 'View Own Scopes' },
  'Session.Close': { key: 'Session.Close', label: 'Close Own Session' },
  'Session.CloseAny': { key: 'Session.CloseAny', label: 'Close Any Session' },
  'Session.Extend': { key: 'Session.Extend', label: 'Extend Session' },
  'Session.Start': { key: 'Session.Start', label: 'Start Session' },
  'Session.View': { key: 'Session.View', label: 'View Own Sessions' },
  'Session.ViewAll': { key: 'Session.ViewAll', label: 'View All Sessions' },
  'System.Seed': { key: 'System.Seed', label: 'Seed Database' },
  'System.ViewLogs': { key: 'System.ViewLogs', label: 'View System Logs' },
  'TeachingStream.Control': { key: 'TeachingStream.Control', label: 'Control Teaching Stream' },
  'Training.Manage': { key: 'Training.Manage', label: 'Manage Training' },
  'Training.View': { key: 'Training.View', label: 'View Training' },
  'User.Create': { key: 'User.Create', label: 'Create User' },
  'User.Delete': { key: 'User.Delete', label: 'Delete User' },
  'User.ManageRoles': { key: 'User.ManageRoles', label: 'Manage User Roles' },
  'User.Update': { key: 'User.Update', label: 'Update User' },
  'User.UpdateOwn': { key: 'User.UpdateOwn', label: 'Update Own Profile' },
  'User.ViewAll': { key: 'User.ViewAll', label: 'View All Users' },
  'User.ViewOwn': { key: 'User.ViewOwn', label: 'View Own Profile' },
  'Video.Observer': { key: 'Video.Observer', label: 'Video Observer' },
  'Video.Presenter': { key: 'Video.Presenter', label: 'Video Presenter' },
  'Video.PrivateStreamer': { key: 'Video.PrivateStreamer', label: 'Video Private Streamer' },
};

/**
 * Single-letter codes the backend packs into `UserDTO.assignedRoles` (pipe-separated).
 *
 * `T` still stands for the institution admin: the backend's own suffix constants in
 * `Models/Role.cs` are commented out, so no live encoding disagrees with the letter.
 * The letter is an opaque tag, not an abbreviation of the current role name. Unassigned for
 * the 5 roles this resync added — see the file header.
 */
export type RoleCode = 'H' | 'I' | 'T' | 'L' | 'S' | 'U';

export type RoleName =
  | 'SmartSashAdmin'
  | 'Installer'
  | 'InstitutionAdmin'
  | 'LabManager'
  | 'LabAssistant'
  | 'DeviceUser'
  | 'TechSupport'
  | 'AccountingFinance'
  | 'HealthSafetyOfficer'
  | 'BuildingMaintenance'
  | 'GroupManager'
  | 'FumeHoodCertifier';

export interface RoleDef {
  label: string;
  /** Present only for the hierarchy roles the backend encodes as a code. */
  code?: RoleCode;
  /** `'all'` = the `FullAccess` wildcard; otherwise the explicit grant set. */
  permissions: readonly PermissionKey[] | 'all';
  /** External, time-limited account (Fume Hood Certifier) — see Q-01. */
  special?: boolean;
}

export const ROLES: Record<RoleName, RoleDef> = {
  SmartSashAdmin: { label: 'SmartSash Admin', code: 'H', permissions: 'all' },
  Installer: {
    label: 'Installer',
    code: 'I',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Calibration.View',
      'Calibration.Write',
      'Device.Create',
      'Device.Delete',
      'Device.Register',
      'Device.Unlock',
      'Device.Update',
      'Device.View',
      'Device.ViewAll',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Group.Manage',
      'Group.View',
      'Installation.AcceptanceTest',
      'Installation.Configure',
      'Installation.View',
      'Institution.AdministerHierarchy',
      'Institution.Create',
      'Institution.Manage',
      'Institution.Update',
      'Institution.View',
      'Institution.ViewAll',
      'Lab.Create',
      'Lab.Delete',
      'Lab.Update',
      'Lab.View',
      'Lab.ViewAll',
      'LanVav.Configure',
      'LanVav.View',
      'Maintenance.View',
      'Report.View',
      'System.Seed',
      'System.ViewLogs',
      'User.ViewAll',
    ],
  },
  InstitutionAdmin: {
    label: 'Institution Admin',
    code: 'T',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Billing.Export',
      'Billing.View',
      'Camera.GrantTechAccess',
      'Camera.ViewOther',
      'Camera.ViewOwn',
      'Certification.Create',
      'Certification.Delete',
      'Certification.Update',
      'Certification.ViewAll',
      'Certification.ViewOwn',
      'CertificationProgram.Manage',
      'CertificationProgram.ViewAll',
      'CertifierVisit.Create',
      'CertifierVisit.Manage',
      'Device.Create',
      'Device.Delete',
      'Device.Register',
      'Device.Unlock',
      'Device.Update',
      'Device.View',
      'Device.ViewAll',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Group.Manage',
      'Group.View',
      // HS-1543: read-only installations, default-enabled; no write key (HS-1621).
      'Installation.View',
      'Institution.AdministerHierarchy',
      'Institution.Manage',
      'Institution.Update',
      'Institution.View',
      'Institution.ViewAggregates',
      'Institution.ViewAll',
      'Lab.Create',
      'Lab.Delete',
      'Lab.Update',
      'Lab.View',
      'Lab.ViewAll',
      'Maintenance.Manage',
      'Maintenance.View',
      'Permission.Assign',
      'Permission.ViewAll',
      'Permission.ViewOwn',
      'Recording.ManageOther',
      'Recording.ViewOwn',
      'RemoteAccess.View',
      'Report.Export',
      'Report.View',
      'Request.Manage',
      'Request.View',
      'Rfid.AssignCard',
      'Rfid.CheckStatus',
      'Rfid.CreateCard',
      'Rfid.RemoveCard',
      'Rfid.ViewAll',
      'Rfid.ViewOwn',
      'SafetyDocument.Edit',
      'SafetyLog.Export',
      'SafetyLog.View',
      'SafetyNotification.Publish',
      'Scope.Assign',
      'Scope.ViewAll',
      'Scope.ViewOwn',
      'Session.Close',
      'Session.CloseAny',
      'Session.Extend',
      'Session.Start',
      'Session.View',
      'Session.ViewAll',
      'System.ViewLogs',
      'TeachingStream.Control',
      'Training.Manage',
      'Training.View',
      'User.Create',
      'User.Delete',
      'User.ManageRoles',
      'User.Update',
      'User.UpdateOwn',
      'User.ViewAll',
      'User.ViewOwn',
      'Video.Observer',
      'Video.Presenter',
      'Video.PrivateStreamer',
    ],
  },
  LabManager: {
    label: 'Lab Manager',
    code: 'L',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Camera.GrantTechAccess',
      'Camera.ViewOther',
      'Camera.ViewOwn',
      'Certification.Create',
      'Certification.Delete',
      'Certification.Update',
      'Certification.ViewAll',
      'Certification.ViewOwn',
      'CertificationProgram.Manage',
      'CertificationProgram.ViewAll',
      'CertifierVisit.Create',
      'CertifierVisit.Manage',
      'Device.Create',
      'Device.Register',
      'Device.Unlock',
      'Device.Update',
      'Device.View',
      'Device.ViewAll',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Lab.Update',
      'Lab.View',
      'Lab.ViewAll',
      'Maintenance.Manage',
      'Maintenance.View',
      'Permission.Assign',
      'Permission.ViewAll',
      'Permission.ViewOwn',
      'Recording.ManageOther',
      'Recording.ViewOwn',
      'RemoteAccess.View',
      'Report.Export',
      'Report.View',
      'Request.View',
      'Rfid.AssignCard',
      'Rfid.CheckStatus',
      'Rfid.CreateCard',
      'Rfid.RemoveCard',
      'Rfid.ViewAll',
      'Rfid.ViewOwn',
      'RfidOverride.Execute',
      'SafetyDocument.Edit',
      'SafetyLog.Export',
      'SafetyLog.View',
      'SashOverride.Execute',
      'Scope.Assign',
      'Scope.ViewAll',
      'Scope.ViewOwn',
      'Session.Close',
      'Session.CloseAny',
      'Session.Extend',
      'Session.Start',
      'Session.View',
      'Session.ViewAll',
      'TeachingStream.Control',
      'Training.Manage',
      'Training.View',
      'User.Create',
      'User.ManageRoles',
      'User.Update',
      'User.UpdateOwn',
      'User.ViewAll',
      'User.ViewOwn',
      'Video.Observer',
      'Video.Presenter',
      'Video.PrivateStreamer',
    ],
  },
  LabAssistant: {
    label: 'Lab Assistant',
    code: 'S',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Camera.ViewOwn',
      'Certification.ViewAll',
      'Certification.ViewOwn',
      'CertificationProgram.ViewAll',
      'Device.View',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Lab.View',
      'Permission.ViewOwn',
      'Recording.ViewOwn',
      'RemoteAccess.View',
      'Report.View',
      'Rfid.CheckStatus',
      'Rfid.ViewOwn',
      'SafetyLog.Export',
      'SafetyLog.View',
      'Scope.ViewOwn',
      'Session.Close',
      'Session.Extend',
      'Session.Start',
      'Session.View',
      'Training.Manage',
      'Training.View',
      'User.UpdateOwn',
      'User.ViewOwn',
      'Video.Observer',
      'Video.Presenter',
      'Video.PrivateStreamer',
    ],
  },
  DeviceUser: {
    label: 'Device User',
    code: 'U',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Camera.GrantTechAccess',
      'Camera.ViewOwn',
      'Certification.ViewOwn',
      'Device.View',
      'File.Delete',
      'File.Upload',
      'Lab.View',
      'Permission.ViewOwn',
      'Recording.ViewOwn',
      'RemoteAccess.View',
      // No `Report.View` (HS-961): the roles matrix (Confluence 266731521) gives a Device User no
      // reports, and the developer decided it wins over the seeder, which still default-enables
      // it (`DatabaseSeeder.cs`, "5.12 DeviceUser"). This bundle feeds tests and labels only —
      // a live session is gated on what `/Auth/me` reports.
      'Rfid.CheckStatus',
      'Rfid.ViewOwn',
      'Scope.ViewOwn',
      'Session.Close',
      'Session.Extend',
      'Session.Start',
      'Session.View',
      'TeachingStream.Control',
      'Training.View',
      'User.UpdateOwn',
      'User.ViewOwn',
      'Video.Observer',
      'Video.Presenter',
      'Video.PrivateStreamer',
    ],
  },
  TechSupport: {
    label: 'Tech Support',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Calibration.View',
      'Camera.ViewOther',
      'Device.View',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Installation.AcceptanceTest',
      'Installation.Configure',
      'Installation.View',
      'LanVav.View',
      'Maintenance.View',
      'Report.View',
      'SafetyLog.View',
    ],
  },
  AccountingFinance: {
    label: 'Accounting & Finance',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Billing.Export',
      'Billing.View',
      'File.Delete',
      'File.Upload',
      'Institution.View',
      'Permission.ViewOwn',
      'Report.Export',
      'Report.View',
      'Scope.ViewOwn',
      'User.UpdateOwn',
      'User.ViewOwn',
    ],
  },
  HealthSafetyOfficer: {
    label: 'Health & Safety Officer',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Certification.ViewAll',
      'Device.View',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'LanVav.View',
      'Maintenance.View',
      'Permission.ViewOwn',
      'Report.View',
      'SafetyDocument.Edit',
      'SafetyLog.Export',
      'SafetyLog.View',
      'SafetyNotification.Publish',
      'Scope.ViewOwn',
      'Training.Manage',
      'Training.View',
      'User.UpdateOwn',
      'User.ViewOwn',
    ],
  },
  BuildingMaintenance: {
    label: 'Building Maintenance',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Calibration.View',
      'Calibration.Write',
      'Certification.ViewAll',
      'Device.View',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'LanVav.View',
      'Maintenance.Manage',
      'Maintenance.View',
      'Permission.ViewOwn',
      'Report.View',
      'Scope.ViewOwn',
      'User.UpdateOwn',
      'User.ViewOwn',
    ],
  },
  GroupManager: {
    label: 'Group Manager',
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Admin',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Camera.GrantTechAccess',
      'Camera.ViewOther',
      'Camera.ViewOwn',
      'Certification.Create',
      'Certification.Update',
      'Certification.ViewAll',
      'Certification.ViewOwn',
      'CertificationProgram.Manage',
      'CertificationProgram.ViewAll',
      'Device.View',
      'Device.ViewAll',
      'Diagnostics.View',
      'File.Delete',
      'File.Upload',
      'Group.View',
      'Lab.Create',
      'Lab.Delete',
      'Lab.Update',
      'Lab.View',
      'Lab.ViewAll',
      'Maintenance.Manage',
      'Maintenance.View',
      'Permission.Assign',
      'Permission.ViewAll',
      'Permission.ViewOwn',
      'Recording.ManageOther',
      'Recording.ViewOwn',
      'RemoteAccess.View',
      'Report.Export',
      'Report.View',
      'Rfid.AssignCard',
      'Rfid.CheckStatus',
      'Rfid.CreateCard',
      'Rfid.RemoveCard',
      'Rfid.ViewAll',
      'Rfid.ViewOwn',
      'SafetyLog.View',
      'Scope.Assign',
      'Scope.ViewAll',
      'Scope.ViewOwn',
      'Session.Close',
      'Session.CloseAny',
      'Session.Extend',
      'Session.Start',
      'Session.View',
      'Session.ViewAll',
      'TeachingStream.Control',
      'Training.Manage',
      'Training.View',
      'User.Create',
      'User.ManageRoles',
      'User.Update',
      'User.UpdateOwn',
      'User.ViewAll',
      'User.ViewOwn',
      'Video.Observer',
      'Video.Presenter',
      'Video.PrivateStreamer',
    ],
  },
  FumeHoodCertifier: {
    label: 'Fume Hood Certifier',
    special: true,
    permissions: [
      'AccessControl.Check',
      'Auth.Login.Device',
      'Auth.Login.Portal',
      'Auth.Refresh',
      'Auth.TwoFactor',
      'Certification.ViewAll',
      'CertificationRecord.Finalize',
      'Device.View',
      'File.Delete',
      'File.Upload',
      'Permission.ViewOwn',
      'Rfid.CheckStatus',
      'Rfid.ViewOwn',
      'Scope.ViewOwn',
      'User.UpdateOwn',
      'User.ViewOwn',
    ],
  },
};

/**
 * The organizational hierarchy, **highest first** — a frontend ordering, not an authority.
 *
 * It used to mirror `UserController.RolePriority` in `sash-admin-api`, which computed the retired
 * single `UserDTO.role`. HS-1565 deleted `RolePriority`. The rank that decides which roles a caller
 * may assign now lives in the backend only, and the admin reads its result, in rank order, from
 * `GET /api/User/assignable-roles` — never derive "what may this caller grant" from this tuple: the
 * backend rank also places `GroupManager` and the functional roles, which this tuple does not.
 *
 * What it still orders: the Users list's role-filter vocabulary and the default assignment the
 * permissions screen preselects. Renamed to the resynced role names (HS-1384), same 6 roles in the
 * same relative order — not extended to the 5 roles that resync added, see the file header.
 * `FumeHoodCertifier` stays outside it — an external, time-limited account (Q-01), not seeded.
 *
 * The order is load-bearing, so it is a tuple rather than the key order of `ROLES`, which is only
 * a convention a reformat could break.
 */
export const ROLE_HIERARCHY = [
  'SmartSashAdmin',
  'Installer',
  'InstitutionAdmin',
  'LabManager',
  'LabAssistant',
  'DeviceUser',
] as const satisfies readonly RoleName[];

/** A role that holds a rank in `ROLE_HIERARCHY`. */
export type HierarchyRoleName = (typeof ROLE_HIERARCHY)[number];

/**
 * Where a role sits in the hierarchy: `0` is the highest, and a **larger number is lower**.
 * `null` for anything outside it — an auth-mechanism marker (`SashDevice`, `Authenticated`,
 * `FunctionAuthorization`), the special certifier account, one of the 5 roles this resync added
 * (not yet ranked, see the file header), or a name the catalogue does not know.
 *
 * Callers must handle `null` explicitly rather than defaulting it to a number: "unranked" is not
 * "lowest", and treating it as either end silently grants or withholds.
 */
export function roleRank(name: string): number | null {
  const index = (ROLE_HIERARCHY as readonly string[]).indexOf(name);
  return index === -1 ? null : index;
}

/** Reverse map: single-letter code → role. */
export const ROLE_CODES: Record<RoleCode, RoleName> = {
  H: 'SmartSashAdmin',
  I: 'Installer',
  T: 'InstitutionAdmin',
  L: 'LabManager',
  S: 'LabAssistant',
  U: 'DeviceUser',
};
